import React, { useState, useEffect } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { Button, Row, Col, Modal } from 'react-bootstrap';
import { InputSelect, FormikSelect, FormikTimePicker, FormikInput } from '../../../../components';
import { delayMethod } from '../../../../services/utils';
import { debounceIndexShiftsRequest } from '../../../../requests/shifts';
import { debounceIndexEmployeesRequest } from '../../../../requests/employees';

const MassiveCreateAttendanceForm = props => {
  const {
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    handleModalClose,
    onHide,
    isSubmitting,
    branchOffices,
    values
  } = props;
  const dispatch = useDispatch();
  const [branchOfficeId, setBranchOfficeId] = useState(null);
  const [employeesFiltered, setEmployeesFiltered] = useState([]);
  const [defaultEmployees, setDefaultEmployees] = useState([]);
  const { attendance: vAttendance } = values;

  const handleNewMulti = (data, field, allowEmpty = false) => {
    if (allowEmpty && !data.length) data.push('');
    const existAll = data.some(v => v.value === 'all');
    if (existAll) setFieldValue(field, [{ label: 'Todos', value: 'all' }]);
    else setFieldValue(field, data);
  };

  const resultFetchData = (response, type) => {
    const result = response.data.data;
    return result.map(element => ({
      ...element,
      label: `${type ? `${element.code} - ` : ''} ${element.name}`,
      value: element.id
    }));
  };

  const fetchContractShift = (inputValue, callback) => {
    debounceIndexShiftsRequest({
      dispatch,
      params: {
        active: true,
        query: inputValue,
        sort_column: 'name',
        paginate: false
      },
      successCallback: data => {
        callback(resultFetchData(data));
      }
    });
  };

  const fetchEmployees = () => {
    if (branchOfficeId !== null) {
      debounceIndexEmployeesRequest({
        dispatch,
        params: {
          is_dt: false,
          active: true,
          filter_active_branch_offices: branchOfficeId,
          sort_column: 'name',
          paginate: false
        },
        successCallback: response => {
          const result = response.data.data;
          const mapResult = result.map(element => ({
            ...element,
            label: `${element.short_name}`,
            value: element.id
          }));

          setEmployeesFiltered(mapResult);
        }
      });
    }
  };

  useEffect(fetchEmployees, [branchOfficeId]);

  const setCommonValues = (field, data, setValueFunc) => {
    setValueFunc(data.value);
    setFieldValue(field.name, data ? data.value : '');
    setDefaultEmployees([]);
  };

  return (
    <>
      {isSubmitting && <div className="loader">Loading...</div>}
      <Form>
        <Modal.Body>
          <Row>
            <Col md={12} className="d-grid">
              <span>
                <b>Nueva Hora de Marcación</b>
              </span>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Field name="attendance[branchOfficeId]">
                {({ field }) => (
                  <FormikSelect
                    {...field}
                    abbr
                    label="Lugar de Prestación de Servicios"
                    placeholder="Seleccionar Lugar de Prestación de Servicios"
                    options={branchOffices}
                    onChange={data => {
                      setCommonValues(field, data, setBranchOfficeId);
                    }}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
            <Col md={6}>
              <Field name="attendance[shiftId]">
                {({ field }) => (
                  <InputSelect
                    {...field}
                    label="Turno"
                    placeholder="Seleccionar Turno"
                    value={undefined}
                    request={fetchContractShift}
                    onChange={data => {
                      setFieldValue(field.name, data ? data.value : '');
                      setDefaultEmployees([]);
                    }}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Field name="attendance[employeeIds]">
                {({ field }) => (
                  <FormikSelect
                    {...field}
                    abbr
                    isMulti
                    label="Colaboradores"
                    placeholder="Seleccionar Colaboradores"
                    options={employeesFiltered}
                    defaultMultiValues={defaultEmployees}
                    value={defaultEmployees}
                    onChange={data => {
                      handleNewMulti(data || [], field.name, true);
                      setDefaultEmployees(data);
                    }}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
            <Col md={6}>
              <Field name="attendance[date]">
                {({ field }) => (
                  <FormikTimePicker
                    {...field}
                    abbr
                    label="Fecha Inicio de Jornada"
                    timeCaption="Hora"
                    placeholder="dd/mm/aaaa"
                    isClearable
                    dateFormat="dd/MM/yyyy"
                    selected={vAttendance?.date ? new Date(vAttendance?.date) : null}
                    onChange={date => setFieldValue(field.name, date || '')}
                    setFieldTouched={() => setFieldTouched(field.name)}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
          </Row>
          <Row>
            <Col md={12} className="d-grid mb-1 mt-1">
              <span>
                <b>Marcaciones Reales Colaborador</b>
              </span>
            </Col>
          </Row>
          <Row className="datetime">
            <Col md={3}>
              <Field name="attendance[parsedArrival]">
                {({ field }) => (
                  <FormikTimePicker
                    {...field}
                    abbr
                    label="Entrada"
                    showTimeSelect
                    timeIntervals={1}
                    timeCaption="Hora"
                    timeFormat="p"
                    isClearable
                    dateFormat="Pp"
                    selected={vAttendance?.arrival ? new Date(vAttendance?.arrival) : null}
                    onChange={date => {
                      setFieldValue(field.name, date ? date.toLocaleString('en-GB') : '');
                      setFieldValue('attendance[arrival]', date || '');
                    }}
                    setFieldTouched={() => setFieldTouched(field.name)}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
            <Col md={3}>
              <Field name="attendance[parsedArrivalBreak]">
                {({ field }) => (
                  <FormikTimePicker
                    {...field}
                    abbr
                    label="Entrada Colación"
                    showTimeSelect
                    timeIntervals={1}
                    timeCaption="Hora"
                    timeFormat="p"
                    dateFormat="Pp"
                    isClearable
                    selected={vAttendance?.arrivalBreakTime ? new Date(vAttendance?.arrivalBreakTime) : null}
                    onChange={date => {
                      setFieldValue(field.name, date ? date.toLocaleString('en-GB') : '');
                      setFieldValue(`attendance[arrivalBreakTime]`, date || '');
                    }}
                    setFieldTouched={() => setFieldTouched(field.name)}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
            <Col md={3}>
              <Field name="attendance[parsedDepartureBreak]">
                {({ field }) => (
                  <FormikTimePicker
                    {...field}
                    abbr
                    label="Salida Colación"
                    showTimeSelect
                    timeIntervals={1}
                    timeCaption="Hora"
                    timeFormat="p"
                    dateFormat="Pp"
                    isClearable
                    selected={vAttendance?.departureBreakTime ? new Date(vAttendance?.departureBreakTime) : null}
                    onChange={date => {
                      setFieldValue(field.name, date ? date.toLocaleString('en-GB') : '');
                      setFieldValue(`attendance[departureBreakTime]`, date || '');
                    }}
                    setFieldTouched={() => setFieldTouched(field.name)}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
            <Col md={3}>
              <Field name="attendance[parsedDeparture]">
                {({ field }) => (
                  <FormikTimePicker
                    {...field}
                    abbr
                    label="Salida"
                    showTimeSelect
                    timeIntervals={1}
                    timeCaption="Hora"
                    timeFormat="p"
                    dateFormat="Pp"
                    isClearable
                    selected={vAttendance?.departure ? new Date(vAttendance?.departure) : null}
                    onChange={date => {
                      setFieldValue(field.name, date ? date.toLocaleString('en-GB') : '');
                      setFieldValue(`attendance[departure]`, date || '');
                    }}
                    setFieldTouched={() => setFieldTouched(field.name)}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
            <Col md={12}>
              <Field name="attendance[comment]">
                {({ field }) => (
                  <FormikInput
                    {...field}
                    label="Motivo"
                    abbr
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="text-center"
            variant="outline-info"
            disabled={isSubmitting}
            onClick={() => delayMethod(handleModalClose)}
          >
            Cancelar
          </Button>
          <Button type="submit" variant="primary" onClick={onHide} disabled={isSubmitting}>
            Agregar
          </Button>
        </Modal.Footer>
      </Form>
    </>
  );
};

const setInitialValues = () => {
  return {
    attendance: {
      employeeIds: [],
      date: '',
      branchOfficeId: '',
      shiftId: '',
      parsedArrival: '',
      parsedDeparture: '',
      parsedArrivalBreak: '',
      parsedDepartureBreak: '',
      arrival: '',
      departure: '',
      arrivalBreakTime: '',
      departureBreakTime: '',
      comment: ''
    }
  };
};

const validationSchema = Yup.object().shape({
  attendance: Yup.object().shape({
    comment: Yup.string()
      .required('Este campo es requerido')
      .min(5, 'El motivo debe tener al menos 5 caracteres')
      .max(255, 'El motivo debe tener máximo 255 caracteres'),
    employeeIds: Yup.array()
      .required('Debes escoger los Colaboradores')
      .min(1, 'Debes escoger al menos un colaborador.'),
    date: Yup.string().required('La Fecha Inicio de Jornada es requerida'),
    branchOfficeId: Yup.string().required('Debes seleccionar un lugar de prestación de servicios'),
    shiftId: Yup.string().required('Debes seleccionar un turno'),
    parsedArrival: Yup.string().test('check-days', 'Ingrese al menos un horario.', function oneBoolean(value) {
      const { parsedDepartureBreak, parsedArrivalBreak, parsedDeparture } = this.parent;
      if (value || parsedDepartureBreak || parsedArrivalBreak || parsedDeparture) {
        return true;
      }
      return false;
    }),
    parsedDepartureBreak: Yup.string().test('check-days', 'Ingrese al menos un horario.', function oneBoolean(value) {
      const { parsedArrival, parsedArrivalBreak, parsedDeparture } = this.parent;
      if (value || parsedArrival || parsedArrivalBreak || parsedDeparture) {
        return true;
      }
      return false;
    }),
    parsedArrivalBreak: Yup.string().test('check-days', 'Ingrese al menos un horario.', function oneBoolean(value) {
      const { parsedDepartureBreak, parsedArrival, parsedDeparture } = this.parent;
      if (value || parsedDepartureBreak || parsedArrival || parsedDeparture) {
        return true;
      }
      return false;
    }),
    parsedDeparture: Yup.string().test('check-days', 'Ingrese al menos un horario.', function oneBoolean(value) {
      const { parsedDepartureBreak, parsedArrivalBreak, parsedArrival } = this.parent;
      if (value || parsedDepartureBreak || parsedArrivalBreak || parsedArrival) {
        return true;
      }
      return false;
    })
  })
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  const customValues = {
    attendance: {
      ...values.attendance,
      employeeIds: values.attendance.employeeIds.map(e => e.value)
    }
  };

  formRequest(customValues, setSubmitting);
};

export default withFormik({
  mapPropsToValues: props => setInitialValues(props),
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: false
})(MassiveCreateAttendanceForm);
